import axios from 'axios'

const validateApiConfig = () => {
  const apiUrl = import.meta.env.VITE_API_URL

  if (!apiUrl) {
    if (import.meta.env.DEV) {
      return '/api/v1'
    }
    throw new Error(
      'VITE_API_URL environment variable is required in production/staging environments. ' +
      'Please set it in your .env file or environment configuration.'
    )
  }

  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    try {
      new URL(apiUrl)
    } catch (error) {
      throw new Error(
        `Invalid VITE_API_URL format: "${apiUrl}". ` +
        'It must be a valid URL (e.g., https://api.example.com/v1) or a relative path (e.g., /api/v1).'
      )
    }
  }

  return apiUrl
}

const API_BASE_URL = validateApiConfig()

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

export const setAccessToken = () => {}
export const getAccessToken = () => null

api.interceptors.request.use(
  (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname
      if (currentPath === '/login' || currentPath.startsWith('/oauth')) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return axios(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error('Token refresh failed')
        }

        processQueue(null, null)
        isRefreshing = false

        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api

// API methods
export const apiMethods = {
  // Auth methods
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  refresh: (refreshToken) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),
  me: () => api.get('/auth/me'),

  // Page methods
  getPages: () => api.get('/pages'),
  getPage: (id) => api.get(`/pages/${id}`),
  createPage: (data) => api.post('/pages', data),
  updatePage: (id, data) => api.put(`/pages/${id}`, data),
  deletePage: (id) => api.delete(`/pages/${id}`),
  publishPage: (id) => api.post(`/pages/${id}/publish`),
  unpublishPage: (id) => api.post(`/pages/${id}/unpublish`),

  // Block methods
  getBlocks: (pageId, viewMode = 'live', locale = 'en') =>
    api.get('/blocks', {
      params: { page_id: pageId, view_mode: viewMode, locale },
    }),

  getBlock: (id) => api.get(`/blocks/${id}`),
  createBlock: (data) => api.post('/blocks', data),
  updateBlock: (id, data) => api.put(`/blocks/${id}`, data),
  deleteBlock: (id, locale = 'en') =>
    api.delete(`/blocks/${id}`, { params: { locale } }),
  publishBlock: (id) => api.post(`/blocks/${id}/publish`),
  unpublishBlock: (id) => api.post(`/blocks/${id}/unpublish`),
  publishAllRevisions: () => api.post('/blocks/publish-all-revisions'),

  // Block revision methods
  getBlockRevisions: (blockId, locale = 'en', limit = 10) =>
    api.get(`/blocks/${blockId}/revisions`, {
      params: { locale, limit },
    }),

  rollbackToRevision: (blockId, revisionId, locale) =>
    api.post(`/blocks/${blockId}/revisions/${revisionId}/rollback`, {
      locale,
    }),

  deleteUnpublishedRevisions: (blockId, locale) =>
    api.delete(`/blocks/${blockId}/unpublished-revisions`, {
      params: { locale },
    }),

  // Block type methods
  getBlockTypes: () => api.get('/block-types'),
  getBlockType: (id) => api.get(`/block-types/${id}`),
  createBlockType: (data) => api.post('/block-types', data),
  updateBlockType: (id, data) => api.put(`/block-types/${id}`, data),
  deleteBlockType: (id) => api.delete(`/block-types/${id}`),

  // Language methods
  getLanguages: () => api.get('/languages'),
  getLanguage: (id) => api.get(`/languages/${id}`),
  createLanguage: (data) => api.post('/languages', data),
  updateLanguage: (id, data) => api.put(`/languages/${id}`, data),
  deleteLanguage: (id) => api.delete(`/languages/${id}`),

  // Field type methods
  getFieldTypes: () => api.get('/field-types'),
  getFieldType: (id) => api.get(`/field-types/${id}`),
  createFieldType: (data) => api.post('/field-types', data),
  updateFieldType: (id, data) => api.put(`/field-types/${id}`, data),
  deleteFieldType: (id) => api.delete(`/field-types/${id}`),

  // User management methods
  getUsers: (params) => api.get('/users', { params }).then((res) => res.data),
  getUser: (id) => api.get(`/users/${id}`).then((res) => res.data),
  createUser: (data) => api.post('/users', data).then((res) => res.data),
  updateUser: (id, data) =>
    api.put(`/users/${id}`, data).then((res) => res.data),
  deleteUser: (id) => api.delete(`/users/${id}`).then((res) => res.data),
  updateUserRole: (id, role) =>
    api.patch(`/users/${id}/role`, { role }).then((res) => res.data),
  toggleUserStatus: (id) =>
    api.patch(`/users/${id}/status`).then((res) => res.data),

  // Role methods
  getRoles: () => api.get('/roles').then((res) => res.data),
  getRole: (id) => api.get(`/roles/${id}`).then((res) => res.data),

  // Menu permission methods
  getMenuPermissions: () =>
    api.get('/menu-permissions').then((res) => res.data),
  getAvailableMenus: () =>
    api.get('/menu-permissions/available-menus').then((res) => res.data),
  updateMenuPermissions: (updates) =>
    api
      .post('/menu-permissions/bulk-update', { updates })
      .then((res) => res.data),

  // Settings methods
  getSettings: () => api.get('/settings').then((res) => res.data),
  getUrlSlugs: () => api.get('/settings/url-slugs').then((res) => res.data),
  updateUrlSlugs: (data) =>
    api.put('/settings/url-slugs', data).then((res) => res.data),
  updateSettings: (settings) =>
    api.put('/settings', { settings }).then((res) => res.data),
}
